import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const StarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
  </svg>
);

const values = [
  {
    title: 'Excellence',
    description: 'We hold ourselves to the highest academic and personal standards. Every student is pushed to discover and exceed their own potential.',
  },
  {
    title: 'Creativity',
    description: 'Through the arts, technology, and collaborative projects, we nurture innovative thinking and self-expression in every learner.',
  },
  {
    title: 'Community',
    description: 'PS 96 is a family. We build strong relationships between students, families, staff, and the broader East Harlem neighborhood.',
  },
  {
    title: 'Equity',
    description: 'Every child deserves access to a world-class education. We are committed to removing barriers and ensuring every student thrives.',
  },
  {
    title: 'Collaboration',
    description: 'We believe learning happens together. Students, teachers, families, and partners work side by side toward shared goals.',
  },
  {
    title: 'Belonging',
    description: 'Our school celebrates the diversity of East Harlem. Every student is seen, valued, and welcomed exactly as they are.',
  },
];

const MissionStatementPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">About Us</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Mission &amp; Vision
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            The purpose, principles, and promise that guide everything we do at PS 96.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 mt-12 mb-16">
        <div className="bg-white border border-gray-200 rounded-xl shadow-md p-10 lg:p-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/10 rounded-lg border border-[#fdbb2e]/30 mb-6">
            <StarIcon />
            <span className="text-sm font-semibold text-[#00213a]">Our Mission</span>
          </div>
          <blockquote>
            <p className="text-3xl font-semibold text-gray-900 leading-snug tracking-tight mb-6" style={{ letterSpacing: '-0.01em' }}>
              At PS 96 we believe that all students have the ability to achieve high academic success.
            </p>
            <div className="space-y-5 text-gray-700 leading-relaxed text-lg">
              <p>
                Based on this belief, our school is one in which all community members are accountable for students making the strides necessary to achieve greatness. Through teaching that relies on personalized instruction, frequent checks of student understanding, and high levels of student engagement, our school can ensure that when our students leave our school they are prepared for success in high school, in college, and in their career endeavors.
              </p>
              <p>
                We are a small learning community that recognizes the inherent struggles that face our students each day and yet we will not offer any excuses for not reaching the highest achievement levels.
              </p>
              <p className="text-xl font-semibold text-[#00213a]">
                We will settle for nothing less than greatness for each and every student that is educated in our classrooms.
              </p>
            </div>
          </blockquote>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-[#00213a] py-20 mb-0">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg border border-white/20 mb-6">
                <span className="text-sm font-semibold text-white">Our Vision</span>
              </div>
              <h2 className="text-4xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
                A School Where Every Child Thrives
              </h2>
              <p className="text-white/80 leading-relaxed text-lg">
                We envision PS 96 as a place where curiosity is celebrated, where students from all backgrounds find belonging, and where the arts and technology are not extras — they are the foundation of a complete education.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {['Arts Integration', 'Dual Language', 'STEM Learning', 'Community Roots'].map((item) => (
                <div key={item} className="bg-white/10 border border-white/20 rounded-xl p-5">
                  <p className="text-white font-semibold">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 mb-4">
              <span className="text-sm font-semibold text-gray-700">Core Values</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              What We Stand For
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200">
                <div className="w-10 h-10 rounded-lg bg-[#fdbb2e] flex items-center justify-center text-[#00213a] mb-4">
                  <StarIcon />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default MissionStatementPage;
